import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { selectCheckAvailabilityDetails } from "app/slice/selectors";
import { useGlobalSlice } from "app/slice";
import { toast } from "app/components/ui/use-toast";

import { Form } from "app/components/ui/form";
import { Label } from "app/components/ui/label";
import { Checkbox } from "app/components/ui/checkbox";
import { Input } from "app/components/ui/input";
import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";

const selectionOptions = ["I want a new number", "I want to port my number"];

type LineFormData = {
  selectedLabel: number | null;
  mdn: string;
  isValidated: boolean;
};

type Props = {
  lineNumber: number;
  lineFormData: LineFormData;
  setLineFormData: (updated: LineFormData) => void;
};

export const NumberSelector = ({
  lineNumber,
  lineFormData,
  setLineFormData,
}: Props) => {
  const dispatch = useDispatch();
  const form = useForm<LineFormData>();
  const zipCodeData = useSelector(selectCheckAvailabilityDetails);
  const { useValidatePortingMutation, actions } = useGlobalSlice();

  const [
    validatePorting,
    {
      data: validatedData,
      isLoading: validateLoading,
      isSuccess: validateSuccess,
      isError: validateError,
      error: validateErrorMessage,
      reset: resetMutation,
    },
  ] = useValidatePortingMutation();

  const setLineFormDataRef = useRef(setLineFormData);
  const lineFormDataRef = useRef(lineFormData);
  setLineFormDataRef.current = setLineFormData;
  lineFormDataRef.current = lineFormData;

  // Handle success and error states with toast notifications
  useEffect(() => {
    if (
      validateSuccess &&
      validatedData?.data?.data?.RESULT?.responseDetails?.eligibilityFlag
    ) {
      dispatch(actions.setIsPorting(true));
      dispatch(actions.setPortRequestSubmitted(true));
      toast({
        title: "Success",
        description:
          "Number porting validation successful! Your number is eligible for porting.",
        variant: "success",
      });
    }
  }, [validateSuccess, validatedData, dispatch, actions]);

  useEffect(() => {
    if (validateError && validateErrorMessage) {
      toast({
        title: "Error",
        description:
          (validateErrorMessage as string) ||
          "Failed to validate number porting. Please try again.",
        variant: "failure",
      });
    }
  }, [validateError, validateErrorMessage]);

  const handleSelectionChange = (index: number) => {
    form.reset();
    if (resetMutation) resetMutation();
    setLineFormData({
      selectedLabel: index,
      mdn: "",
      isValidated: false,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey) {
      return;
    }

    if (
      [
        "Backspace",
        "Delete",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ].includes(e.key)
    ) {
      return;
    }

    if (!/\d/.test(e.key)) {
      e.preventDefault();
    }
  };

  const submitPhoneTypeForm = (data: any) => {
    if (lineFormData.selectedLabel === 1 && data.mdn) {
      const body = {
        zip_code:
          zipCodeData?.service_availability?.wholeSaleApi?.wholeSaleResponse
            ?.result?.zipCode,
        mdn: data.mdn.replace(/\D/g, ""),
        source: "WEBSITE",
      };
      validatePorting(body);
      setLineFormData({ ...lineFormData, mdn: data.mdn });
    }
  };

  useEffect(() => {
    if (!validateSuccess) return;

    const eligible = Boolean(
      validatedData?.data?.data?.RESULT?.responseDetails?.eligibilityFlag
    );

    const current = lineFormDataRef.current;
    if (current.isValidated === eligible) return;
    setLineFormDataRef.current({ ...current, isValidated: eligible });
  }, [validateSuccess, validatedData]);

  useEffect(() => {
    if (!validateError) return;

    const current = lineFormDataRef.current;
    if (current.isValidated === false) return;
    setLineFormDataRef.current({ ...current, isValidated: false });
  }, [validateError]);

  return (
    <div className="bg-white w-full p-4 sm:p-5 lg:p-6 border border-[#E5E7EB] rounded-xl md:rounded-2xl">
      <h4 className="text-xl font-bold leading-5 text-black sm:text-[1.35rem] lg:text-2xl sm:leading-6 lg:leading-7 font-mobile mb-4 sm:mb-5 lg:mb-[1.875rem]">
        Type of Number {lineNumber > 0 && <>(Line {lineNumber})</>}
      </h4>
      <Form {...form}>
        <form
          className="space-y-4 sm:space-y-[1.125rem] lg:space-y-5"
          onSubmit={form.handleSubmit(submitPhoneTypeForm)}
        >
          <div className="space-y-4 sm:space-y-[1.125rem] lg:space-y-5">
            {selectionOptions.map((label, index) => {
              const id = `selection-${lineNumber}-${index}`;
              return (
                <div
                  key={id}
                  className="flex items-center gap-2 sm:gap-[0.625rem]"
                >
                  <Checkbox
                    id={id}
                    checked={lineFormData.selectedLabel === index}
                    onCheckedChange={(val: boolean) => {
                      if (val) handleSelectionChange(index);
                    }}
                    className="w-5 h-5 md:w-[1.35rem] md:h-[1.35rem] lg:w-6 lg:h-6 rounded-[0.25rem] border-[#1D1D1B]"
                  />
                  <Label
                    htmlFor={id}
                    className="text-[#1D1D1B] text-sm md:text-[0.935rem] lg:text-base font-semibold leading-5 md:leading-6 mb-0 cursor-pointer"
                  >
                    {label}
                  </Label>
                </div>
              );
            })}
          </div>
          {lineFormData.selectedLabel === 1 && (
            <div className="space-y-4 sm:space-y-[1.125rem] lg:space-y-5">
              <div className="">
                <Label
                  htmlFor={`phoneNumber-${lineNumber}`}
                  className="w-fit text-black text-xs sm:text-[0.825rem] md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
                >
                  Enter Number
                </Label>
                <div className="">
                  <div className="relative">
                    <Input
                      id={`phoneNumber-${lineNumber}`}
                      type="tel"
                      inputMode="numeric"
                      maxLength={14}
                      {...form.register("mdn", {
                        required: "Phone number is required!",
                        pattern: {
                          value: /^\(\d{3}\)\s\d{3}-\d{4}$/,
                          message:
                            "Please enter a valid phone number in format (XXX) XXX-XXXX",
                        },
                      })}
                      onChange={(e) => {
                        const numbers = e.target.value.replace(/\D/g, "");
                        const formattedNumber = numbers.replace(
                          /(\d{0,3})(\d{0,3})(\d{0,4})/,
                          (_, p1, p2, p3) => {
                            if (p3) return `(${p1}) ${p2}-${p3}`;
                            if (p2) return `(${p1}) ${p2}`;
                            if (p1) return `(${p1}`;
                            return "";
                          }
                        );
                        e.target.value = formattedNumber;
                        form.register("mdn").onChange(e);
                      }}
                      onKeyDown={handleKeyDown}
                      className={`bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                        form.formState.errors.mdn
                          ? "border-[#D80027] focus:outline-[#D80027]"
                          : "border-[#D1D5DB] focus:outline-[#707C8F]"
                      }`}
                      disabled={validateLoading || validateSuccess}
                    />
                    <Button
                      type="submit"
                      variant="ghost"
                      className={`absolute right-3 md:right-3.5 top-1/2 -translate-y-1/2 bg-transparent hover:bg-transparent text-[#FE4D01] hover:text-[#E44501] text-[0.72rem] sm:text-xs font-semibold leading-4 sm:leading-5 ${
                        validateSuccess ? "disabled:opacity-100" : ""
                      }`}
                      disabled={validateLoading || validateSuccess}
                    >
                      {validateLoading ? (
                        <div className="flex items-center gap-0.5">
                          <span>Verifying</span>
                          <Icons.Spinner className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                        </div>
                      ) : validateSuccess ? (
                        <img
                          src="/assets/tick-circle.svg"
                          alt="check"
                          className="size-4 sm:size-[1.125rem] lg:size-5"
                        />
                      ) : (
                        "Verify"
                      )}
                    </Button>
                  </div>
                  {form.formState?.errors?.mdn && (
                    <p className="text-[#D80027] text-[0.72rem] sm:text-xs font-normal leading-5 mt-0.5 sm:mt-1">
                      <span className="font-bold">*</span>
                      {form.formState?.errors?.mdn?.message as string}
                    </p>
                  )}
                </div>
              </div>
              {validateSuccess &&
              validatedData?.data?.data?.RESULT?.responseDetails
                ?.eligibilityFlag ? (
                <div className="flex gap-1.5 sm:gap-2 md:gap-[0.625rem] bg-[#F2FFEC] p-2.5 sm:p-3 lg:p-[0.875rem] rounded-lg sm:rounded-xl">
                  <div className="flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 mt-0.5 sm:mt-1">
                    <img
                      src="/assets/green-tick.svg"
                      className="w-full h-auto"
                      alt="Tick"
                    />
                  </div>
                  <p className="text-[#3C3C3C] text-[0.725rem] sm:text-xs font-medium leading-4 sm:leading-5">
                    Congratulations! Your number is eligible for porting. We'll
                    start the transfer process after checkout.
                  </p>
                </div>
              ) : validateError ? (
                <div className="flex gap-1.5 sm:gap-2 md:gap-[0.625rem] bg-[#F5F5F5] p-2.5 sm:p-3 lg:p-[0.875rem] rounded-lg sm:rounded-xl">
                  <div className="flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 mt-0.5 sm:mt-1">
                    <img
                      src="/assets/danger.webp"
                      className="w-full h-auto"
                      alt="Tick"
                    />
                  </div>
                  <p className="text-[#3C3C3C] text-[0.725rem] sm:text-xs font-medium leading-4 sm:leading-5">
                    We're sorry, there was an issue verifying port eligibility
                    of your number. Try again later or contact support for
                    assistance. Thank you for your patience.
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </form>
      </Form>
    </div>
  );
};
